import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';

// Reupholster Dining Chair — per Quoting_markdown/reupholster_dining_chairs.md.
// Config drives which of Seat/Inside Back/Outside Back sections apply. A
// chair back is one physical panel with two faces, so Inside Back and
// Outside Back share one Width/Height input. Submitted through dedicated
// addReupholsterDiningChairDraftQuoteObject/
// updateReupholsterDiningChairDraftQuoteObject mutations, same deviation
// Bench/Cornice/Bed/Reupholster Bench/Ottoman/Pop Seat already established.
const ReupholsterDiningChairForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;

  const [config, setConfig] = useState<string>(iv?.diningChairConfig || '');
  const [width, setWidth] = useState(iv?.widthInches != null ? String(iv.widthInches) : '');
  const [depth, setDepth] = useState(iv?.depthInches != null ? String(iv.depthInches) : '');
  const [backWidth, setBackWidth] = useState(iv?.backWidthInches != null ? String(iv.backWidthInches) : '');
  const [backHeight, setBackHeight] = useState(iv?.backHeightInches != null ? String(iv.backHeightInches) : '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [fabricWidth, setFabricWidth] = useState(iv?.fabricWidthInches != null ? String(iv.fabricWidthInches) : '54');
  const [patternRepeat, setPatternRepeat] = useState(iv?.patternRepeatInches != null ? String(iv.patternRepeatInches) : '');

  const [seatFinish, setSeatFinish] = useState<string>(iv?.seatFinish || 'none');
  const [seatWeltPlacement, setSeatWeltPlacement] = useState<string>(iv?.seatWeltPlacement || 'bottom');
  const [bandingSize, setBandingSize] = useState<string>(iv?.bandingSize || '');
  const [seatNailheadColor, setSeatNailheadColor] = useState<string>(iv?.seatNailheadColor || '');

  const [insideBackFinish, setInsideBackFinish] = useState<string>(iv?.insideBackFinish || 'none');
  const [insideBackNailheadColor, setInsideBackNailheadColor] = useState<string>(iv?.insideBackNailheadColor || '');

  const [outsideBackFinish, setOutsideBackFinish] = useState<string>(iv?.outsideBackFinish || 'none');
  const [outsideBackNailheadColor, setOutsideBackNailheadColor] = useState<string>(iv?.outsideBackNailheadColor || '');

  const [skirt, setSkirt] = useState<boolean>(iv?.skirt || false);
  const [skirtDropInches, setSkirtDropInches] = useState(iv?.skirtDropInches != null ? String(iv.skirtDropInches) : '');
  const [touchUpWood, setTouchUpWood] = useState<boolean>(iv?.touchUpWood || false);
  const [refinishOrPaint, setRefinishOrPaint] = useState<boolean>(iv?.refinishOrPaint || false);
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  const hasInsideBack = config === 'seat_and_inside_back' || config === 'seat_inside_outside_back';
  const hasOutsideBack = config === 'seat_inside_outside_back';

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!config) { setError('Config is required'); return null; }
      if (!width) { setError('Seat Width is required'); return null; }
      if (!depth) { setError('Seat Depth is required'); return null; }
      if (hasInsideBack && !backWidth) { setError('Back Width is required'); return null; }
      if (hasInsideBack && !backHeight) { setError('Back Height is required'); return null; }
      if (skirt && !skirtDropInches) { setError('Skirt Drop is required'); return null; }
      setError(null);
      return {
        diningChairConfig: config,
        widthInches: parseFloat(width),
        depthInches: parseFloat(depth),
        backWidthInches: hasInsideBack && backWidth ? parseFloat(backWidth) : null,
        backHeightInches: hasInsideBack && backHeight ? parseFloat(backHeight) : null,
        fabricDescription: fabricDescription.trim() || null,
        fabricWidthInches: fabricWidth ? parseFloat(fabricWidth) : null,
        patternRepeatInches: patternRepeat ? parseFloat(patternRepeat) : null,
        seatFinish,
        seatWeltPlacement: seatFinish === 'welt' ? seatWeltPlacement : null,
        bandingSize: seatFinish === 'banding' ? (bandingSize.trim() || null) : null,
        seatNailheadColor: seatFinish === 'nailheads' ? (seatNailheadColor.trim() || null) : null,
        insideBackFinish: hasInsideBack ? insideBackFinish : null,
        insideBackNailheadColor: hasInsideBack && insideBackFinish === 'nailheads' ? (insideBackNailheadColor.trim() || null) : null,
        outsideBackFinish: hasOutsideBack ? outsideBackFinish : null,
        outsideBackNailheadColor: hasOutsideBack && outsideBackFinish === 'nailheads' ? (outsideBackNailheadColor.trim() || null) : null,
        skirt,
        skirtDropInches: skirt && skirtDropInches ? parseFloat(skirtDropInches) : null,
        touchUpWood,
        refinishOrPaint,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Config</label>
          <select
            className="input"
            value={config}
            onChange={(e) => setConfig(e.target.value)}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="seat_only">Seat Only</option>
            <option value="seat_and_inside_back">Seat and Inside Back</option>
            <option value="seat_inside_outside_back">Seat, Inside and Outside Back</option>
          </select>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Seat Width (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={width}
            onChange={(e) => setWidth(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Seat Depth (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={depth}
            onChange={(e) => setDepth(e.target.value)} disabled={props.disabled} />
        </div>
      </div>

      {hasInsideBack && (
        <div className="add-item-form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Back Width (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(shared by inside/outside)</span></label>
            <input type="number" min="0" step="0.25" className="input" value={backWidth}
              onChange={(e) => setBackWidth(e.target.value)} disabled={props.disabled} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Back Height (inches)</label>
            <input type="number" min="0" step="0.25" className="input" value={backHeight}
              onChange={(e) => setBackHeight(e.target.value)} disabled={props.disabled} />
          </div>
        </div>
      )}

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={fabricDescription}
            onChange={(name) => setFabricDescription(name)}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ width: 120 }}>
          <label>Fabric Width (in)</label>
          <input type="number" min="0" step="1" className="input" value={fabricWidth}
            onChange={(e) => setFabricWidth(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ width: 140 }}>
          <label>Pattern Repeat (in) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input type="number" min="0" step="0.25" className="input" value={patternRepeat}
            onChange={(e) => setPatternRepeat(e.target.value)} disabled={props.disabled} />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Seat Finish</label>
          <select
            className="input"
            value={seatFinish}
            onChange={(e) => setSeatFinish(e.target.value)}
            disabled={props.disabled}
          >
            <option value="none">None (plain, $120)</option>
            <option value="banding">Banding ($195)</option>
            <option value="welt">Welt — top or bottom ($216)</option>
            <option value="welt_both">Welt — top and bottom ($312)</option>
            <option value="nailheads">Nailheads ($312)</option>
          </select>
        </div>
        {seatFinish === 'banding' && (
          <div className="form-group" style={{ width: 160 }}>
            <label>Banding Size</label>
            <input type="text" className="input" placeholder={'e.g. 2"'} value={bandingSize}
              onChange={(e) => setBandingSize(e.target.value)} disabled={props.disabled} />
          </div>
        )}
        {seatFinish === 'welt' && (
          <div className="form-group" style={{ width: 140 }}>
            <label>Welt Placement</label>
            <select className="input" value={seatWeltPlacement}
              onChange={(e) => setSeatWeltPlacement(e.target.value)} disabled={props.disabled}>
              <option value="top">Top</option>
              <option value="bottom">Bottom</option>
            </select>
          </div>
        )}
        {seatFinish === 'nailheads' && (
          <div className="form-group" style={{ width: 160 }}>
            <label>Nailhead Color</label>
            <input type="text" className="input" value={seatNailheadColor}
              onChange={(e) => setSeatNailheadColor(e.target.value)} disabled={props.disabled} />
          </div>
        )}
      </div>

      {hasInsideBack && (
        <div className="add-item-form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Inside Back Finish <span style={{ color: '#9ca3af', fontWeight: 400 }}>($120 base + $96 if any finish selected)</span></label>
            <select
              className="input"
              value={insideBackFinish}
              onChange={(e) => setInsideBackFinish(e.target.value)}
              disabled={props.disabled}
            >
              <option value="none">None</option>
              <option value="banding">Banding</option>
              <option value="welt">Welt</option>
              <option value="double_welt">Double Welt</option>
              <option value="gimp">Gimp</option>
              <option value="nailheads">Nailheads</option>
              <option value="trim">Trim</option>
            </select>
          </div>
          {insideBackFinish === 'nailheads' && (
            <div className="form-group" style={{ width: 160 }}>
              <label>Nailhead Color</label>
              <input type="text" className="input" value={insideBackNailheadColor}
                onChange={(e) => setInsideBackNailheadColor(e.target.value)} disabled={props.disabled} />
            </div>
          )}
        </div>
      )}

      {hasOutsideBack && (
        <div className="add-item-form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Outside Back Finish <span style={{ color: '#9ca3af', fontWeight: 400 }}>($120 base + $96 if any finish selected)</span></label>
            <select
              className="input"
              value={outsideBackFinish}
              onChange={(e) => setOutsideBackFinish(e.target.value)}
              disabled={props.disabled}
            >
              <option value="none">None</option>
              <option value="welt">Welt</option>
              <option value="nailheads">Nailheads</option>
            </select>
          </div>
          {outsideBackFinish === 'nailheads' && (
            <div className="form-group" style={{ width: 160 }}>
              <label>Nailhead Color</label>
              <input type="text" className="input" value={outsideBackNailheadColor}
                onChange={(e) => setOutsideBackNailheadColor(e.target.value)} disabled={props.disabled} />
            </div>
          )}
        </div>
      )}

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={skirt}
              onChange={(e) => setSkirt(e.target.checked)} disabled={props.disabled} />
            {' '}With skirt (+$200 flat)
          </label>
        </div>
        {skirt && (
          <div className="form-group" style={{ width: 160 }}>
            <label>Skirt Drop (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(seat to floor)</span></label>
            <input type="number" min="0" step="0.25" className="input" value={skirtDropInches}
              onChange={(e) => setSkirtDropInches(e.target.value)} disabled={props.disabled} />
          </div>
        )}
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={touchUpWood}
              onChange={(e) => setTouchUpWood(e.target.checked)} disabled={props.disabled} />
            {' '}Touch up wood (+$25 each)
          </label>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={refinishOrPaint}
              onChange={(e) => setRefinishOrPaint(e.target.checked)} disabled={props.disabled} />
            {' '}Refinish or paint (+$300 per chair)
          </label>
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default ReupholsterDiningChairForm;
